class ComponentHeader extends Theme.Component {
  connectedCallback() {
    super.connectedCallback();

    this.collapsible = this.querySelector(".collapse");

    this.watchHeight();
    this.watchCollapse();
    this.watchSentinel();
    this.watchTone();
  }

  disconnectedCallback() {
    this.unwatchCollapse();
    this.unwatchSentinel();
    this.unwatchHeight();

    super.disconnectedCallback();
  }

  watchHeight() {
    this.setHeight();

    this.resizeObserver = new ResizeObserver(Theme.Helpers.debounce(() => {
      if (this.collapsing || this.classList.contains("is-mega-open") || this.isHoverExpanded()) {
        return;
      }

      this.setHeight();
      this.measure();
      this.watchSentinel();
    }, 350));

    this.resizeObserver.observe(this);
  }

  unwatchHeight() {
    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
    this.resizeObserver = null;
  }

  isHoverExpanded() {
    return (
      this.classList.contains("is-scrolled") &&
      window.matchMedia("(min-width: 1024px) and (pointer: fine)").matches &&
      this.querySelector(".navigation:is(:hover, :focus-within)") !== null
    );
  }

  baseHeight() {
    return Theme.querySelectorAll("[data-megamenu-panel]", this).reduce(
      (height, panel) => height - panel.offsetHeight,
      this.offsetHeight
    );
  }

  setHeight(height = this.baseHeight()) {
    document.documentElement.style.setProperty("--fixed-header-height", `${height}px`);
  }

  measure() {
    if (!this.collapsible || this.classList.contains("is-scrolled")) {
      return;
    }

    this.collapsedBy = this.collapsible.offsetHeight;
    this.expandedHeight = this.baseHeight();

    document.documentElement.style.setProperty("--announcement-height", `${this.collapsedBy}px`);
    document.documentElement.style.setProperty("--navigation-height", `${this.expandedHeight - this.collapsedBy}px`);
  }

  setCollapsedHeight(collapsed) {
    if (!this.collapsible || !this.expandedHeight) {
      return;
    }

    this.setHeight(collapsed ? this.expandedHeight - this.collapsedBy : this.expandedHeight);
  }

  watchCollapse() {
    if (!this.collapsible) {
      document.documentElement.style.setProperty("--announcement-height", "0px");
      return;
    }

    this.measure();

    this.onCollapseStart = (event) => {
      if (event.target !== this.collapsible || event.propertyName !== "grid-template-rows") {
        return;
      }

      this.collapsing = true;
    };

    this.onCollapseEnd = (event) => {
      if (event.target !== this.collapsible || event.propertyName !== "grid-template-rows") {
        return;
      }

      this.collapsing = false;

      this.setHeight();
      this.measure();
      this.watchSentinel();
    };

    this.collapsible.addEventListener("transitionstart", this.onCollapseStart);
    this.collapsible.addEventListener("transitionend", this.onCollapseEnd);
    this.collapsible.addEventListener("transitioncancel", this.onCollapseEnd);
  }

  unwatchCollapse() {
    if (!this.collapsible) {
      return;
    }

    this.collapsible.removeEventListener("transitionstart", this.onCollapseStart);
    this.collapsible.removeEventListener("transitionend", this.onCollapseEnd);
    this.collapsible.removeEventListener("transitioncancel", this.onCollapseEnd);

    this.collapsible = null;
    this.collapsing = false;
  }

  watchSentinel() {
    const sentinel = document.querySelector("[data-header-sentinel]");

    if (!sentinel) {
      return;
    }

    this.unwatchSentinel();

    this.sentinelObserver = new IntersectionObserver((entries) => this.onSentinel(entries[0]), {
      root: null,
      threshold: 0,
      rootMargin: `${Math.ceil(this.baseHeight()) + 60}px 0px 0px 0px`
    });

    this.sentinelObserver.observe(sentinel);
  }

  unwatchSentinel() {
    if (!this.sentinelObserver) {
      return;
    }

    this.sentinelObserver.disconnect();
    this.sentinelObserver = null;
  }

  watchTone() {
    this.navigation = this.querySelector(".navigation");

    if (!this.navigation) {
      return;
    }

    this.measureTone();

    this.addDirectEvent("load", window, this.measureTone);
    this.addDirectEvent("resize", window, Theme.Helpers.debounce(this.measureTone, 150));
    this.addDirectEvent("shopify:section:load", document, this.measureTone);
  }

  navigationBounds() {
    const bounds = this.navigation.getBoundingClientRect();
    const offset = this.classList.contains("is-scrolled") ? this.collapsedBy || 0 : 0;

    return {
      top: bounds.top + offset,
      left: bounds.left,
      width: bounds.width,
      height: bounds.height
    };
  }

  toneImage(bounds) {
    const section = document.querySelector("main .shopify-section");

    if (!section) {
      return null;
    }

    const centre = bounds.left + bounds.width / 2;

    return Theme.querySelectorAll("img", section).find((image) => {
      const rect = image.getBoundingClientRect();
      const top = rect.top + window.scrollY;

      return (
        rect.width > 0 &&
        top <= bounds.top &&
        top + rect.height >= bounds.top + bounds.height &&
        rect.left <= centre &&
        rect.right >= centre
      );
    });
  }

  async measureTone() {
    const request = (this.toneRequest = (this.toneRequest || 0) + 1);
    const bounds = this.navigationBounds();
    const image = this.toneImage(bounds);

    if (!image || !bounds.width || !bounds.height) {
      this.setTone(false);
      return;
    }

    const source = new Image();

    source.crossOrigin = "anonymous";

    const loaded = await new Promise((resolve) => {
      source.onload = () => resolve(true);
      source.onerror = () => resolve(false);
      source.src = image.currentSrc || image.src;
    });

    if (!loaded) {
      return;
    }

    if (request !== this.toneRequest) {
      return;
    }

    this.setTone(this.luminance(source, image, bounds) < 0.5);
  }

  luminance(source, image, bounds) {
    const rect = image.getBoundingClientRect();
    const [focusX, focusY] = getComputedStyle(image).objectPosition.split(" ").map((value) => {
      return value.endsWith("%") ? parseFloat(value) / 100 : 0.5;
    });

    const scale = Math.max(rect.width / source.naturalWidth, rect.height / source.naturalHeight);
    const width = source.naturalWidth * scale;
    const height = source.naturalHeight * scale;
    const left = rect.left + (rect.width - width) * focusX;
    const top = rect.top + window.scrollY + (rect.height - height) * focusY;

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true });

    canvas.width = 64;
    canvas.height = 8;

    const ratioX = canvas.width / bounds.width;
    const ratioY = canvas.height / bounds.height;

    context.drawImage(
      source,
      (left - bounds.left) * ratioX,
      (top - bounds.top) * ratioY,
      width * ratioX,
      height * ratioY
    );

    let pixels;

    try {
      pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    } catch {
      return 1;
    }

    let total = 0;

    for (let index = 0; index < pixels.length; index += 4) {
      total += (0.2126 * pixels[index] + 0.7152 * pixels[index + 1] + 0.0722 * pixels[index + 2]) / 255;
    }

    return total / (pixels.length / 4);
  }

  setTone(light) {
    this.navigation.classList.toggle("navigation--light", light);
  }

  onSentinel(entry) {
    if (!entry) {
      return;
    }

    this.classList.toggle("is-scrolled", !entry.isIntersecting);
    this.setCollapsedHeight(!entry.isIntersecting);
  }
}

customElements.define("component-header", ComponentHeader);
